/**
 * m365ConfigFactsApi.ts - AutoDoc "Configuration des services M365".
 * Faits lus en direct via Microsoft Graph / Exchange Admin REST pour les
 * documents Exchange Online, SharePoint/OneDrive et Teams.
 */
import { apiFetch } from './apiClient';

// --- Exchange Online ---------------------------------------------------------
export interface ExoDomain { name: string; domainName: string; domainType: string; default: boolean; }
export interface ExoDkim { domain: string; enabled: boolean; status: string; }
export interface ExoDns {
  domain: string;
  spf:   { found: boolean; record?: string | null };
  dmarc: { found: boolean; record?: string | null; policy?: string | null };
}
export interface ExoAntiSpam {
  name: string; default: boolean; bulkThreshold?: number | null;
  spamAction?: string; highConfidenceSpamAction?: string; phishSpamAction?: string; highConfidencePhishAction?: string;
}
export interface ExoAntiPhish {
  name: string; enabled: boolean; phishThresholdLevel?: number | null;
  mailboxIntelligence?: boolean; spoofIntelligence?: boolean; targetedUserProtection?: boolean; dmarcRejectAction?: string;
}
export interface ExoConnector { name: string; enabled: boolean; connectorType: string; }
export interface ExoConfigFacts {
  acceptedDomains: ExoDomain[];
  dkim:            ExoDkim[];
  dns:             ExoDns[];
  antiSpam:        ExoAntiSpam[];
  antiPhish:       ExoAntiPhish[];
  connectors:      { inbound: ExoConnector[]; outbound: ExoConnector[] };
  summary?:        { domains: number; dkimEnabled: number; spfOk: number; dmarcOk: number; connectors: number };
}

// --- SharePoint / OneDrive ---------------------------------------------------
export interface SpoConfigFacts {
  sharing: {
    sharingCapability?: string; sharingDomainRestrictionMode?: string;
    sharingAllowedDomainList?: string[]; sharingBlockedDomainList?: string[];
    isResharingByExternalUsersEnabled?: boolean; defaultSharingLinkType?: string; defaultLinkPermission?: string;
    externalUserExpirationRequired?: boolean; externalUserExpireInDays?: number | null;
    fileAnonymousLinkType?: string; folderAnonymousLinkType?: string; anonymousLinkExpirationInDays?: number | null;
  };
  sync: {
    isMacSyncAppEnabled?: boolean; isUnmanagedSyncAppForTenantRestricted?: boolean;
    allowedDomainGuidsForSyncApp?: string[]; excludedFileExtensionsForSyncApp?: string[];
  };
  tenant: Record<string, unknown>;
}

// --- Teams -------------------------------------------------------------------
export interface TeamsConfigFacts {
  inventory: { total: number; public: number; private: number; sample: { displayName: string; visibility: string }[] };
  guestAccess: {
    allowInvitesFrom?: string; guestUserRole?: string; blockMsolPowerShell?: boolean;
    allowToAddGuests?: boolean; allowGuestsToAccessGroups?: boolean; enableGroupCreation?: boolean;
  };
  manualSections: string[];
}

export interface FactsResponse<T> {
  success:   boolean;
  tenantId?: string;
  runAt?:    string;
  facts?:    T;
  warnings?: { fact: string; code?: number; error?: string }[];
  error?:    string;
  hint?:     string;
}

export const m365ConfigFactsApi = {
  exo:   () => apiFetch<FactsResponse<ExoConfigFacts>>('/exo-config-facts'),
  spo:   () => apiFetch<FactsResponse<SpoConfigFacts>>('/spo-config-facts'),
  teams: () => apiFetch<FactsResponse<TeamsConfigFacts>>('/teams-config-facts'),
};
